import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Checkbox, FormControlLabel, Switch, TextField } from "@mui/material";

import {
    fetchTenantAuth,
    fetchTenantAuthImpact,
    updateTenantAuth
} from "../../../api/admin/tenant/endpoints-referrers";
import {
    DEFAULT_PASSWORD_POLICY,
    PasswordPolicy,
    SIGN_IN_METHOD_LABELS,
    SignInMethod,
    TenantAuthProvider,
    TenantAuthSettings,
    TenantDefaultAssistant,
    TenantDomainImpact
} from "../../../common/interfaces/admin/tenant.interface";
import TenantSsoProviders from "../tenant-management/tenant-sso-providers.comp";
import TenantDefaultAssistants from "../tenant-management/tenant-default-assistants.comp";
import { StringListInput } from "../../../components/admin/string-list-input/string-list-input.comp";
import { captureMicrosoftDirectoryId } from "../../../common/utilities/tenant-auth/capture-microsoft-directory.util";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import { ToastContainer } from "../../../components/toast/toast.comp";

type PasswordFlag = "requireUppercase" | "requireLowercase" | "requireNumeric" | "requireSymbol";

// Same rules as the backend's normalizeDomain / normalizeDirectoryId.
const normalizeDomainInput = (raw: string) => {
    const d = raw.trim().toLowerCase().replace(/^@/, "");
    return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d) ? d : "";
};
const normalizeDirectoryIdInput = (raw: string) => {
    const id = raw.trim().toLowerCase();
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(id) ? id : "";
};

const domainImpactDescription = (impact: TenantDomainImpact | null) => {
    if (!impact) return "";
    const parts: string[] = [];
    if (impact.count > 0) {
        const names = impact.users
            .map(u => `${u.email}${u.role === "admin" ? " (admin)" : ""}`)
            .join(", ");
        parts.push(
            `${impact.count} existing user${impact.count === 1 ? "" : "s"} will be locked out at their next request: ${names}${
                impact.count > impact.users.length ? ", …" : ""
            }.`
        );
    }
    if (impact.pendingInvites > 0) {
        parts.push(
            `${impact.pendingInvites} pending invitation${
                impact.pendingInvites === 1 ? "" : "s"
            } to addresses outside the list can no longer be used.`
        );
    }
    parts.push("User records are kept; removing the domain list restores access.");
    return parts.join(" ");
};

export interface TenantAuthenticationHandle {
    /**
     * Validate, preview the domain impact (D3) if needed, and save. Resolves
     * true when the settings were saved (or there was nothing to save), false when
     * a validation error is showing inline, the save failed (error inline),
     * or the admin cancelled the impact confirmation.
     */
    save: () => Promise<boolean>;
    isDirty: () => boolean;
}

interface TenantAuthenticationProps {
    companyId: number;
    /** Firebase tenant id — enables "Capture from sign-in" for directory pinning. */
    tenantId?: string | null;
    /** Reports every change of the unsaved-changes state (for a Save button). */
    onDirtyChange?: (dirty: boolean) => void;
}

/**
 * The Authentication settings form (tenant_authentication_plan.md §2.4),
 * hosted by Tenant Properties for super admins and by the /authentication
 * page for delegated admins (tenant_auth_delegation_design.md §4.1). It owns
 * load, the form, the inline validation hints, the D3 domain-impact
 * confirmation and the save; the host triggers the save through the ref.
 * The guard behind /admin/tenants/:companyId/auth/* decides who may.
 */
const TenantAuthentication = forwardRef<TenantAuthenticationHandle, TenantAuthenticationProps>(
    ({ companyId, tenantId, onDirtyChange }, ref) => {
        const { showToast } = ToastContainer.useContainer();
        const [authForm, setAuthForm] = useState<TenantAuthSettings | null>(null);
        const [initialAuth, setInitialAuth] = useState<string>("");
        const [loadError, setLoadError] = useState<string>("");
        const [error, setError] = useState<string>("");
        const [isSaving, setIsSaving] = useState<boolean>(false);
        const [domainImpact, setDomainImpact] = useState<TenantDomainImpact | null>(null);
        const [isCapturingDirectory, setIsCapturingDirectory] = useState<boolean>(false);
        // Resolves the pending save() once the impact popup is answered.
        const impactDecision = useRef<((proceed: boolean) => void) | null>(null);
        // If the host unmounts the form while the impact confirm is open, a
        // pending save() must still settle — as "not saved" — or the host's
        // Save stays stuck on the promise.
        useEffect(
            () => () => {
                impactDecision.current?.(false);
                impactDecision.current = null;
            },
            []
        );

        useEffect(() => {
            setAuthForm(null);
            setLoadError("");
            setError("");
            fetchTenantAuth(companyId)
                .then(response => {
                    const settings: TenantAuthSettings = response.data?.authSettings;
                    setAuthForm(settings);
                    setInitialAuth(JSON.stringify(settings));
                })
                .catch(() => setLoadError("Failed to load authentication settings"));
        }, [companyId]);

        const dirty = !!authForm && JSON.stringify(authForm) !== initialAuth;
        useEffect(() => {
            onDirtyChange?.(dirty);
        }, [dirty, onDirtyChange]);

        const enabledMethodCount = authForm
            ? (Object.keys(authForm.methods) as SignInMethod[]).filter(m => authForm.methods[m])
                  .length
            : 0;
        // Phase 4: the SSO method needs an enabled provider behind it (the backend
        // refuses the save otherwise); the providers section reports its rows.
        const [enabledSsoProviderCount, setEnabledSsoProviderCount] = useState<number>(0);
        const onSsoProvidersChanged = useCallback(
            (providers: TenantAuthProvider[]) =>
                setEnabledSsoProviderCount(providers.filter(p => p.isEnabled).length),
            []
        );
        const ssoMethodBlocked =
            !!authForm && authForm.methods.sso && enabledSsoProviderCount === 0;
        // Phase 5: the toggle cannot be on without a default Assistant and a domain list.
        const [defaultAssistantCount, setDefaultAssistantCount] = useState<number>(0);
        const onDefaultAssistantsChanged = useCallback(
            (list: TenantDefaultAssistant[]) => setDefaultAssistantCount(list.length),
            []
        );
        const domainsConfigured = (authForm?.allowedDomains.length ?? 0) > 0;
        // Why the toggle is unavailable (off) or cannot be saved (on): the
        // missing prerequisite, named so the admin knows what to do next.
        let autoProvisionReason = "";
        if (authForm && !domainsConfigured) {
            autoProvisionReason = authForm.autoProvision.enabled
                ? "Automatic user creation needs an allowed-domains list; turn it off or add a domain."
                : "To turn this on, add at least one allowed domain above (press Enter after typing it) and one default Assistant below.";
        } else if (authForm && defaultAssistantCount === 0) {
            autoProvisionReason =
                "Automatic user creation needs at least one default Assistant below.";
        }
        const autoProvisionBlocked =
            !!authForm && authForm.autoProvision.enabled && !!autoProvisionReason;

        const toggleMethod = (method: SignInMethod, checked: boolean) =>
            setAuthForm(prev =>
                prev ? { ...prev, methods: { ...prev.methods, [method]: checked } } : prev
            );
        const setAuthList = (key: "allowedDomains" | "microsoftDirectoryIds", values: string[]) =>
            setAuthForm(prev => (prev ? { ...prev, [key]: values } : prev));
        const setPasswordRule = <K extends keyof PasswordPolicy>(
            key: K,
            value: PasswordPolicy[K]
        ) =>
            setAuthForm(prev =>
                prev
                    ? {
                          ...prev,
                          passwordPolicy: {
                              ...(prev.passwordPolicy ?? DEFAULT_PASSWORD_POLICY),
                              [key]: value
                          }
                      }
                    : prev
            );
        // Drafts for the two numeric rules so a half-typed value does not snap
        // back; committed (clamped) on blur.
        const [minLengthDraft, setMinLengthDraft] = useState<string>("");
        const [expireDaysDraft, setExpireDaysDraft] = useState<string>("");
        useEffect(() => {
            const policy = authForm?.passwordPolicy ?? DEFAULT_PASSWORD_POLICY;
            setMinLengthDraft(String(policy.minLength));
            setExpireDaysDraft(policy.expireDays === null ? "" : String(policy.expireDays));
            // only when a (new) tenant's settings arrive
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [initialAuth]);
        const commitMinLength = () => {
            const n = Math.min(30, Math.max(6, parseInt(minLengthDraft, 10) || 6));
            setMinLengthDraft(String(n));
            setPasswordRule("minLength", n);
        };
        const commitExpireDays = () => {
            const raw = expireDaysDraft.trim();
            if (!raw) {
                setPasswordRule("expireDays", null);
                return;
            }
            const n = Math.min(3650, Math.max(1, parseInt(raw, 10) || 1));
            setExpireDaysDraft(String(n));
            setPasswordRule("expireDays", n);
        };

        // D11: Microsoft on + a domain list ⇒ at least one directory id, or the
        // backend refuses the save. Mirrors tenantAuthSettingsErrors.
        const directoryIdsRequired =
            !!authForm && authForm.allowedDomains.length > 0 && authForm.methods.microsoft;
        const directoryIdsMissing =
            directoryIdsRequired && (authForm?.microsoftDirectoryIds.length ?? 0) === 0;

        // "Capture from sign-in" (§2.6): a Microsoft popup on a secondary Firebase
        // app against THIS tenant, reading the directory id from Microsoft's token.
        const handleCaptureDirectory = async () => {
            if (!tenantId || isCapturingDirectory) return;
            setIsCapturingDirectory(true);
            try {
                const tid = await captureMicrosoftDirectoryId(tenantId);
                setAuthForm(prev =>
                    prev && !prev.microsoftDirectoryIds.includes(tid)
                        ? { ...prev, microsoftDirectoryIds: [...prev.microsoftDirectoryIds, tid] }
                        : prev
                );
                showToast(`Captured directory ${tid}`, 3000);
            } catch (err: any) {
                if (
                    err?.code !== "auth/popup-closed-by-user" &&
                    err?.code !== "auth/cancelled-popup-request"
                ) {
                    showToast(err?.message || "Could not capture the directory id", 4000);
                }
            } finally {
                setIsCapturingDirectory(false);
            }
        };

        const commit = async (): Promise<boolean> => {
            if (!authForm) return false;
            setIsSaving(true);
            setError("");
            try {
                await updateTenantAuth(companyId, authForm);
                setInitialAuth(JSON.stringify(authForm));
                return true;
            } catch (err: any) {
                const raw =
                    err.response?.data?.message ||
                    err.message ||
                    "Failed to save authentication settings";
                // class-validator returns an array of messages.
                setError(Array.isArray(raw) ? raw.join(" ") : String(raw));
                return false;
            } finally {
                setIsSaving(false);
            }
        };

        // D3: a new or changed domain list is previewed before it is saved —
        // the people it would lock out are shown and the admin confirms.
        const save = async (): Promise<boolean> => {
            // Nothing loaded (still loading, or the load failed) is nothing
            // to save — the host's other tabs must not be blocked by it.
            if (!authForm || !dirty) return true;
            setError("");
            if (enabledMethodCount === 0) {
                setError("At least one sign-in method must stay enabled");
                return false;
            }
            if (directoryIdsMissing) {
                setError(
                    "Add at least one Microsoft directory id, or turn Microsoft sign-in off, while an allowed-domains list is set"
                );
                return false;
            }
            const initialDomains: string[] = initialAuth
                ? (JSON.parse(initialAuth) as TenantAuthSettings).allowedDomains
                : [];
            const domainsChanged =
                authForm.allowedDomains.length > 0 &&
                JSON.stringify(authForm.allowedDomains) !== JSON.stringify(initialDomains);
            if (domainsChanged) {
                setIsSaving(true);
                try {
                    const { data } = await fetchTenantAuthImpact(
                        companyId,
                        authForm.allowedDomains
                    );
                    if (data.count > 0 || data.pendingInvites > 0) {
                        setIsSaving(false);
                        setDomainImpact(data);
                        const proceed = await new Promise<boolean>(resolve => {
                            impactDecision.current = resolve;
                        });
                        if (!proceed) return false;
                    }
                } catch (err: any) {
                    setError(
                        err.response?.data?.message || "Could not check who the domain list affects"
                    );
                    setIsSaving(false);
                    return false;
                }
            }
            return commit();
        };

        useImperativeHandle(ref, () => ({ save, isDirty: () => dirty }));

        const answerImpact = (proceed: boolean) => {
            setDomainImpact(null);
            impactDecision.current?.(proceed);
            impactDecision.current = null;
        };

        return (
            <div className="tenant-permissions tenant-auth" aria-busy={isSaving || undefined}>
                <p className="tenant-permissions__hint">
                    How this tenant&apos;s users sign in. A method switched off here is hidden from
                    the login page and refused by the API on the next request, including for users
                    who are already signed in. At least one method must stay enabled.
                </p>
                {loadError && (
                    <p className="tenant-permissions__hint" role="alert">
                        {loadError}
                    </p>
                )}
                {!authForm && !loadError && <p className="tenant-permissions__hint">Loading…</p>}
                {authForm && (
                    <>
                        <div className="tenant-permissions__group">
                            <div
                                className="tenant-permissions__group-title"
                                role="heading"
                                aria-level={3}
                            >
                                Sign-in methods
                            </div>
                            <div className="tenant-permissions__grid">
                                {(Object.keys(SIGN_IN_METHOD_LABELS) as SignInMethod[]).map(
                                    method => (
                                        <FormControlLabel
                                            key={method}
                                            className="tenant-permissions__item"
                                            control={
                                                <Checkbox
                                                    checked={authForm.methods[method]}
                                                    onChange={e =>
                                                        toggleMethod(method, e.target.checked)
                                                    }
                                                />
                                            }
                                            label={SIGN_IN_METHOD_LABELS[method]}
                                        />
                                    )
                                )}
                            </div>
                            {enabledMethodCount === 0 && (
                                <p className="tenant-permissions__hint" role="alert">
                                    At least one sign-in method must stay enabled.
                                </p>
                            )}
                            {ssoMethodBlocked && (
                                <p className="tenant-permissions__hint" role="alert">
                                    SSO can be switched on once at least one SSO provider below is
                                    enabled.
                                </p>
                            )}
                        </div>
                        <TenantSsoProviders
                            companyId={companyId}
                            ssoMethodEnabled={authForm.methods.sso}
                            onProvidersChanged={onSsoProvidersChanged}
                        />
                        <div className="tenant-permissions__group">
                            <div
                                className="tenant-permissions__group-title"
                                role="heading"
                                aria-level={3}
                            >
                                Allowed email domains
                            </div>
                            <p className="tenant-permissions__hint">
                                Leave empty to allow any address. With domains listed, every sign-in
                                method — including existing sessions — is limited to addresses in
                                them, and invitations outside them are refused. Super admins keep
                                support access.
                            </p>
                            <StringListInput
                                label="Domains"
                                values={authForm.allowedDomains}
                                onChange={v => setAuthList("allowedDomains", v)}
                                normalize={normalizeDomainInput}
                                placeholder="example.com"
                                invalidMessage="Not a domain (no @, no wildcards)"
                                hint="Press Enter or comma after each domain"
                            />
                        </div>
                        {directoryIdsRequired && (
                            <div className="tenant-permissions__group">
                                <div
                                    className="tenant-permissions__group-title"
                                    role="heading"
                                    aria-level={3}
                                >
                                    Microsoft directories
                                </div>
                                <p className="tenant-permissions__hint">
                                    Microsoft sign-in is pinned to these Entra directory (tenant)
                                    ids while a domain list is set; an account from any other
                                    directory is refused even if its address is in an allowed
                                    domain. Accounts from a listed directory are treated as
                                    verified.
                                </p>
                                <StringListInput
                                    label="Directory ids"
                                    values={authForm.microsoftDirectoryIds}
                                    onChange={v => setAuthList("microsoftDirectoryIds", v)}
                                    normalize={normalizeDirectoryIdInput}
                                    placeholder="00000000-0000-0000-0000-000000000000"
                                    invalidMessage="Not a directory id (GUID)"
                                />
                                <div className="tenant-auth__capture">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleCaptureDirectory}
                                        disabled={isCapturingDirectory || !tenantId}
                                    >
                                        {isCapturingDirectory
                                            ? "Waiting for Microsoft…"
                                            : "Capture from sign-in"}
                                    </Button>
                                    <span className="tenant-permissions__hint">
                                        Signs in with a Microsoft account from your directory (in a
                                        popup, without affecting your session) and fills in its id.
                                    </span>
                                </div>
                                {directoryIdsMissing && (
                                    <p className="tenant-permissions__hint" role="alert">
                                        At least one directory id is required while Microsoft
                                        sign-in is enabled with a domain list.
                                    </p>
                                )}
                            </div>
                        )}
                        {authForm.methods.password && (
                            <div className="tenant-permissions__group">
                                <div
                                    className="tenant-permissions__group-title"
                                    role="heading"
                                    aria-level={3}
                                >
                                    Password rules
                                </div>
                                <p className="tenant-permissions__hint">
                                    Complexity is enforced by Firebase on sign-up, reset and change;
                                    existing passwords stay valid until they are changed or expire.
                                    Expiry sends a user whose password is older than the window to a
                                    set-a-new-password screen at their next request.
                                </p>
                                <div className="tenant-auth__rules">
                                    <TextField
                                        label="Minimum length"
                                        type="number"
                                        size="small"
                                        inputProps={{ min: 6, max: 30 }}
                                        value={minLengthDraft}
                                        onChange={e => setMinLengthDraft(e.target.value)}
                                        onBlur={commitMinLength}
                                        helperText="6–30 characters"
                                    />
                                    <TextField
                                        label="Expires after (days)"
                                        type="number"
                                        size="small"
                                        inputProps={{ min: 1, max: 3650 }}
                                        value={expireDaysDraft}
                                        onChange={e => setExpireDaysDraft(e.target.value)}
                                        onBlur={commitExpireDays}
                                        placeholder="Never"
                                        helperText="Empty = never expires"
                                    />
                                </div>
                                <div className="tenant-permissions__grid">
                                    {(
                                        [
                                            ["requireUppercase", "An uppercase letter"],
                                            ["requireLowercase", "A lowercase letter"],
                                            ["requireNumeric", "A number"],
                                            ["requireSymbol", "A symbol"]
                                        ] as [PasswordFlag, string][]
                                    ).map(([key, label]) => (
                                        <FormControlLabel
                                            key={key}
                                            className="tenant-permissions__item"
                                            control={
                                                <Checkbox
                                                    checked={
                                                        !!(authForm.passwordPolicy ??
                                                            DEFAULT_PASSWORD_POLICY)[key]
                                                    }
                                                    onChange={e =>
                                                        setPasswordRule(key, e.target.checked)
                                                    }
                                                />
                                            }
                                            label={`Require ${label.toLowerCase()}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="tenant-permissions__group">
                            <div
                                className="tenant-permissions__group-title"
                                role="heading"
                                aria-level={3}
                            >
                                Automatic user creation
                            </div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={authForm.autoProvision.enabled}
                                        disabled={
                                            !domainsConfigured ||
                                            (!authForm.autoProvision.enabled &&
                                                defaultAssistantCount === 0)
                                        }
                                        onChange={e =>
                                            setAuthForm(prev =>
                                                prev
                                                    ? {
                                                          ...prev,
                                                          autoProvision: {
                                                              enabled: e.target.checked
                                                          }
                                                      }
                                                    : prev
                                            )
                                        }
                                    />
                                }
                                label={
                                    authForm.autoProvision.enabled
                                        ? "First logins from an allowed domain create the user automatically"
                                        : "Users must be invited"
                                }
                            />
                            <p className="tenant-permissions__hint">
                                With this on, anyone who signs in with an enabled method and a
                                verified address in the allowed domains gets an account with the
                                User role on the default Assistants below, and the sign-up page
                                works without an invite. Invited users still receive exactly what
                                their invite names. Available only while an allowed-domains list is
                                set.
                            </p>
                            {autoProvisionReason && (
                                <p
                                    className="tenant-permissions__hint"
                                    role={autoProvisionBlocked ? "alert" : undefined}
                                >
                                    {autoProvisionReason}
                                </p>
                            )}
                            <TenantDefaultAssistants
                                companyId={companyId}
                                autoProvisionEnabled={authForm.autoProvision.enabled}
                                domainsConfigured={domainsConfigured}
                                onListChanged={onDefaultAssistantsChanged}
                            />
                        </div>
                        <div className="tenant-permissions__group">
                            <div
                                className="tenant-permissions__group-title"
                                role="heading"
                                aria-level={3}
                            >
                                Support access
                            </div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={authForm.allowSupportAccess}
                                        onChange={e =>
                                            setAuthForm(prev =>
                                                prev
                                                    ? {
                                                          ...prev,
                                                          allowSupportAccess: e.target.checked
                                                      }
                                                    : prev
                                            )
                                        }
                                    />
                                }
                                label={
                                    authForm.allowSupportAccess
                                        ? "Super admins may open support sessions into this tenant"
                                        : "Support sessions are turned off for this tenant"
                                }
                            />
                            <p className="tenant-permissions__hint">
                                A support session is a short-lived, audited admin sign-in that does
                                not depend on the sign-in methods above. With it off, a broken
                                configuration can only be repaired in the database.
                            </p>
                        </div>
                    </>
                )}
                {error && (
                    <div className="create-tenant-form__error" role="alert">
                        {error}
                    </div>
                )}
                <ConfirmationPopup
                    open={!!domainImpact}
                    type={ConfirmationPopupType.dangerous}
                    title="Restrict sign-in to these domains?"
                    subtitle={domainImpact?.domains.join(", ")}
                    description={domainImpactDescription(domainImpact)}
                    actionName="Save anyway"
                    handleAction={() => answerImpact(true)}
                    handleClose={() => answerImpact(false)}
                />
            </div>
        );
    }
);

TenantAuthentication.displayName = "TenantAuthentication";

export default TenantAuthentication;
